import { Listbox } from '@headlessui/react'
import { ChevronDownIcon } from 'lucide-react'
import type { KeyboardEventHandler, RefObject } from 'react'
import { getSelectButtonA11yProps } from './accessibility'
import { cx, selectButtonSizeClassMap } from './classNames'
import type { SelectOption, SelectSize } from './types'
import { renderSelectOptionLabel } from '../selectOptionLabel'

/**
 * Props for the shared listbox trigger button used by plain and searchable
 * selects.
 */
export type SelectButtonProps = {
  valueLabel: string
  selectedOption?: SelectOption
  buttonRef: RefObject<HTMLButtonElement | null>
  buttonId?: string
  ariaLabel?: string
  ariaInvalid?: boolean
  describedById?: string
  onBlur?: () => void
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>
  disabled?: boolean
  size: SelectSize
  emphasizePrimaryLabel: boolean
  buttonClassName?: string
  chevronClassName: string
}

/**
 * Shared button structure for select triggers.
 */
export const SelectButton = ({
  valueLabel,
  selectedOption,
  buttonRef,
  buttonId,
  ariaLabel,
  ariaInvalid,
  describedById,
  onBlur,
  onKeyDown,
  size,
  emphasizePrimaryLabel,
  buttonClassName,
  chevronClassName,
}: SelectButtonProps) => (
  <Listbox.Button
    ref={buttonRef}
    id={buttonId}
    {...getSelectButtonA11yProps({ ariaLabel, ariaInvalid, describedById })}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    className={cx(
      'relative h-11 w-full rounded-xl border px-3 pr-10 text-left text-sm font-medium shadow-sm transition normal-case',
      'border-(--color-border-default,#cbd5e1) bg-(--color-bg-card,#ffffff) text-(--color-text-primary,#1f2937)',
      'hover:border-(--color-border-strong,#94a3b8)',
      'focus:border-(--color-focus-ring,#3b82f6) focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-focus-ring,#3b82f6)_22%,transparent)]',
      'disabled:cursor-not-allowed disabled:opacity-60',
      selectButtonSizeClassMap[size],
      buttonClassName,
    )}
  >
    {selectedOption ? (
      renderSelectOptionLabel(selectedOption, false, emphasizePrimaryLabel)
    ) : (
      <span className="block truncate">{valueLabel}</span>
    )}
    <ChevronDownIcon className={cx('pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2', chevronClassName)} />
  </Listbox.Button>
)
